// Small, reusable pieces shared by the Weekly Site Report editor sections.
import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Camera, X, ChevronDown, Sparkles, ExternalLink, Link2, Loader2, FileText, Image as ImageIcon, Check, Undo2, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { RAG_COLORS, RAG_VALUES, CHECK_VALUES, IMPACT_OPTIONS, RISK_STATUS_OPTIONS, DELAY_REASON_OPTIONS, riskAgeDays, riskAgeTone, programmeVariance, varianceTone } from "./wsrConstants";
import StatusPill from "@/components/Pill";
import { PendingPhotoThumbs } from "@/components/PendingPhotoThumbs";

const INPUT = "w-full rounded-lg border border-[#E5E0DA] bg-white px-2.5 py-1.5 text-[13px] text-[#1F1F1F] focus:outline-none focus:ring-2 focus:ring-[#C75A1B]/25 focus:border-[#C75A1B]/50 disabled:bg-[#F5F3F0] disabled:text-[#8B867E]";

export function Field({ label, children }) {
    return (
        <div>
            {label && <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] mb-1">{label}</div>}
            {children}
        </div>
    );
}

export function TextInput({ value, onChange, onBlur, disabled, placeholder, testId, textarea, type = "text" }) {
    const Comp = textarea ? "textarea" : "input";
    return (
        <Comp data-testid={testId} type={textarea ? undefined : type} className={INPUT + (textarea ? " min-h-[64px] resize-y" : "")}
              value={value || ""} placeholder={placeholder} disabled={disabled}
              onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
    );
}

export function SelectInput({ value, onChange, disabled, options, placeholder = "—", testId }) {
    return (
        <select data-testid={testId} className={INPUT} value={value || ""} disabled={disabled}
                onChange={(e) => onChange(e.target.value)}>
            <option value="">{placeholder}</option>
            {options.map((o) => {
                const val = typeof o === "string" ? o : o.value;
                const label = typeof o === "string" ? o : o.label;
                return <option key={val} value={val}>{label}</option>;
            })}
        </select>
    );
}

export function Badge({ tone = "auto", children }) {
    const TONES = {
        auto: { bg: "#EAF1FB", fg: "#2A5DB0" },
        carried: { bg: "#F2ECFB", fg: "#6B3FA0" },
        fixed: { bg: "#F0ECE5", fg: "#8B867E" },
        suggested: { bg: "#FBF3EC", fg: "#C75A1B" },
        needsAnswer: { bg: "#FBE3E1", fg: "#B3261E" },
    };
    const c = TONES[tone] || TONES.auto;
    return <span className="px-1.5 py-0.5 rounded-md text-[9.5px] font-bold tracking-[.03em]" style={{ background: c.bg, color: c.fg }}>{children}</span>;
}

export function Hint({ text }) {
    const [open, setOpen] = useState(false);
    if (!text) return null;
    return (
        <div className="mb-1.5">
            <button type="button" data-testid="wsr-hint-toggle" onClick={() => setOpen((o) => !o)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8B867E] hover:text-[#C75A1B]">
                <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} /> What to include
            </button>
            {open && <div className="mt-1 text-[11.5px] text-[#6F6A64] bg-[#F5F3F0] rounded-lg p-2 border border-[#E5E0DA]">{text}</div>}
        </div>
    );
}

export function Pill({ active, tone, suggested, onClick, disabled, children, testId }) {
    const c = tone ? RAG_COLORS[tone] : null;
    const style = active
        ? { background: c ? c.bg : "#F2E4D8", color: c ? c.fg : "#9F4212", borderColor: c ? c.border : "#E7C7A8" }
        : suggested
            ? { background: "#FBF3EC", color: "#C75A1B", borderColor: "#C75A1B", borderStyle: "dashed" }
            : { background: "#fff", color: "#8B867E", borderColor: "#E5E0DA" };
    return (
        <button type="button" data-testid={testId} disabled={disabled} onClick={onClick}
                className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold border transition-colors disabled:cursor-default"
                style={style}>
            {children}
        </button>
    );
}

export function CheckPillGroup({ value, onChange, disabled, options = CHECK_VALUES, testId }) {
    return (
        <div className="flex gap-1.5 flex-wrap">
            {options.map((o) => (
                <Pill key={o} active={value === o} onClick={() => !disabled && onChange(o)} disabled={disabled}
                      testId={testId && `${testId}-${o.toLowerCase().replace(/[^a-z]/g, "")}`}
                      tone={o === "Yes" ? "Green" : o === "No" ? "Red" : null}>
                    {o}
                </Pill>
            ))}
        </div>
    );
}

export function RagPillGroup({ value, onChange, disabled, testId }) {
    return (
        <div className="flex gap-1.5">
            {RAG_VALUES.map((o) => (
                <Pill key={o} active={value === o} onClick={() => !disabled && onChange(o)} disabled={disabled}
                      testId={testId && `${testId}-${o.toLowerCase()}`} tone={o}>
                    {o}
                </Pill>
            ))}
        </div>
    );
}

// Checklist grid: [{label}] x {value, note, auto_suggested, edited_by_user} keyed by label.
// showNeedsAnswer + onRaiseAction are opt-in (used by the HS/environmental checklists only —
// Quality/CDM checklists keep the plain behaviour).
export function ChecklistGrid({ items, labels, value, onChange, disabled, testId, showNeedsAnswer, onRaiseAction, raisingItem }) {
    const v = value || {};
    const set = (label, patch) => onChange({ ...v, [label]: { ...(v[label] || {}), ...patch, edited_by_user: true } });
    return (
        <div className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
            {items.map((label, i) => {
                const entry = v[label] || {};
                const needsAnswer = showNeedsAnswer && !entry.value;
                const noNeedsNote = entry.value === "No" && !entry.note;
                return (
                    <div key={label} className={`px-3 py-2 ${i > 0 ? "border-t border-[#F0ECE5]" : ""}`}>
                        <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)] gap-2 items-center">
                            <div className="text-[13px] text-[#1F1F1F] flex items-center gap-1.5 flex-wrap">
                                {labels?.[label] || label}
                                {entry.auto_suggested && !entry.edited_by_user && <Badge tone="suggested"><Sparkles size={9} className="inline -mt-0.5 mr-0.5" />Suggested</Badge>}
                                {needsAnswer && !disabled && <Badge tone="needsAnswer">Needs your answer</Badge>}
                            </div>
                            <CheckPillGroup value={entry.value} onChange={(val) => set(label, { value: val })} disabled={disabled}
                                            testId={testId && `${testId}-${i}`} />
                            <TextInput value={entry.note} onChange={(note) => set(label, { note })} disabled={disabled}
                                       placeholder={entry.value === "No" ? "Note (required)" : "Note (optional)"} testId={testId && `${testId}-${i}-note`} />
                        </div>
                        {onRaiseAction && entry.value === "No" && (
                            <div className="mt-1.5 flex items-center gap-2.5 pl-0.5">
                                {noNeedsNote && <span className="text-[10.5px] font-semibold text-[#B33A2E]">A note is required for a "No" answer</span>}
                                {entry.action_id ? (
                                    <span className="text-[11px] font-semibold text-[#2A5DB0]" data-testid={testId && `${testId}-${i}-action-raised`}>Action {entry.action_ref} raised</span>
                                ) : !disabled && (
                                    <button type="button" data-testid={testId && `${testId}-${i}-raise-action`}
                                            disabled={raisingItem === label}
                                            onClick={() => onRaiseAction(label)}
                                            className="text-[11px] font-semibold text-[#C75A1B] hover:underline disabled:opacity-50">
                                        Raise action
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// Generic add/remove-row table. columns: [{key,label,width,textarea,type:"select",options,readOnly}]
export function DynTable({ columns, rows, onChange, disabled, addLabel = "Add row", testId }) {
    const list = rows || [];
    const update = (idx, key, val) => {
        const next = list.map((r, i) => (i === idx ? { ...r, [key]: val } : r));
        onChange(next);
    };
    const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
    const add = () => onChange([...list, {}]);
    return (
        <div className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="bg-[#F5F3F0] text-left">
                            {columns.map((c) => (
                                <th key={c.key} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]" style={{ width: c.width }}>{c.label}</th>
                            ))}
                            {!disabled && <th className="w-8" />}
                        </tr>
                    </thead>
                    <tbody>
                        {list.length === 0 && (
                            <tr><td colSpan={columns.length + 1} className="px-3 py-3 text-[#8B867E] italic">No entries yet.</td></tr>
                        )}
                        {list.map((row, idx) => (
                            <tr key={idx} className="border-t border-[#F0ECE5] align-top">
                                {columns.map((c) => (
                                    <td key={c.key} className="px-2 py-1.5">
                                        {c.type === "select" ? (
                                            <SelectInput value={row[c.key]} disabled={disabled} options={c.options}
                                                         onChange={(v) => update(idx, c.key, v)} testId={testId && `${testId}-${idx}-${c.key}`} />
                                        ) : (
                                            <TextInput value={row[c.key]} disabled={disabled || c.readOnly} textarea={c.textarea}
                                                       onChange={(v) => update(idx, c.key, v)} testId={testId && `${testId}-${idx}-${c.key}`} />
                                        )}
                                    </td>
                                ))}
                                {!disabled && (
                                    <td className="px-2 py-1.5 text-center">
                                        <button data-testid={testId && `${testId}-remove-${idx}`} onClick={() => remove(idx)}
                                                className="text-[#B33A2E] hover:text-[#8A2A21]"><Trash2 size={15} /></button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!disabled && (
                <button data-testid={testId && `${testId}-add`} onClick={add}
                        className="flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold text-[#C75A1B] hover:bg-[#FBF3EC] w-full">
                    <Plus size={14} /> {addLabel}
                </button>
            )}
        </div>
    );
}

// Auto/carried/fixed-aware table: badges per row, auto rows get tagged
// `edited:true` the moment any cell changes so Refresh never clobbers them.
// Fixed rows can't be removed. Manual "Add row" appends a plain manual row.
export function TaggedTable({ columns, rows, onChange, disabled, addLabel = "Add row", testId, refBadge, rowAction }) {
    const list = rows || [];
    const update = (idx, key, val) => {
        const next = list.map((r, i) => {
            if (i !== idx) return r;
            return { ...r, [key]: val, edited: true };
        });
        onChange(next);
    };
    const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
    const add = () => onChange([...list, { id: `manual-${Date.now()}`, source: "manual" }]);
    return (
        <div data-testid={testId} className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="bg-[#F5F3F0] text-left">
                            <th className="w-8" />
                            {columns.map((c) => (
                                <th key={c.key} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]" style={{ width: c.width }}>{c.label}</th>
                            ))}
                            {!disabled && <th className="w-8" />}
                        </tr>
                    </thead>
                    <tbody>
                        {list.length === 0 && (
                            <tr><td colSpan={columns.length + 2} className="px-3 py-3 text-[#8B867E] italic">No entries yet.</td></tr>
                        )}
                        {list.map((row, idx) => {
                            const isFixed = row.source === "fixed";
                            const rowDisabled = disabled;
                            return (
                                <tr key={row.id || idx} className="border-t border-[#F0ECE5] align-top">
                                    <td className="px-1.5 py-1.5 text-center">
                                        {row.source === "auto" && !row.edited && <Badge tone="auto">Auto · from {refBadge || "system"}</Badge>}
                                        {row.source === "auto" && row.edited && <Badge tone="auto">Edited</Badge>}
                                        {row.source === "carried" && <Badge tone="carried">{row.carried_from ? `From ${row.carried_from}` : "Carried"}</Badge>}
                                        {isFixed && <Badge tone="fixed">Fixed</Badge>}
                                    </td>
                                    {columns.map((c) => {
                                        const opts = c.optionsFromRow ? (row.options || c.options) : c.options;
                                        const fieldReadOnly = rowDisabled || c.readOnly || (c.autoReadOnly && row.source === "auto");
                                        return (
                                            <td key={c.key} className="px-2 py-1.5">
                                                {c.type === "select" ? (
                                                    <SelectInput value={row[c.key]} disabled={fieldReadOnly} options={opts || []}
                                                                 onChange={(v) => update(idx, c.key, v)} testId={testId && `${testId}-${idx}-${c.key}`} />
                                                ) : c.type === "link" ? (
                                                    <div className="space-y-1">
                                                        <TextInput value={row[c.key]} disabled={fieldReadOnly}
                                                                   onChange={(v) => update(idx, c.key, v)} testId={testId && `${testId}-${idx}-${c.key}`} />
                                                        {row[c.key] && (
                                                            <a href={row[c.key]} target="_blank" rel="noreferrer"
                                                               data-testid={testId && `${testId}-${idx}-${c.key}-open`}
                                                               className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2A5DB0] hover:underline">
                                                                Open <ExternalLink size={10} />
                                                            </a>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <TextInput value={row[c.key]} disabled={fieldReadOnly} textarea={c.textarea}
                                                               onChange={(v) => update(idx, c.key, v)} testId={testId && `${testId}-${idx}-${c.key}`} />
                                                )}
                                            </td>
                                        );
                                    })}
                                    {!disabled && (
                                        <td className="px-2 py-1.5 text-center whitespace-nowrap">
                                            {rowAction && rowAction.show(row) && (
                                                <button data-testid={testId && `${testId}-rowaction-${idx}`} onClick={() => rowAction.onClick(row, idx)}
                                                        className="mr-1.5 text-[11px] font-semibold text-[#2A5DB0] hover:underline whitespace-nowrap">
                                                    {rowAction.label}
                                                </button>
                                            )}
                                            {!isFixed && !(rowAction && rowAction.hideDefaultRemove && rowAction.show(row)) && (
                                                <button data-testid={testId && `${testId}-remove-${idx}`} onClick={() => remove(idx)}
                                                        className="text-[#B33A2E] hover:text-[#8A2A21]"><Trash2 size={15} /></button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {!disabled && (
                <button data-testid={testId && `${testId}-add`} onClick={add}
                        className="flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold text-[#C75A1B] hover:bg-[#FBF3EC] w-full">
                    <Plus size={14} /> {addLabel}
                </button>
            )}
        </div>
    );
}

function _isOverdue(dateStr) {
    if (!dateStr) return false;
    try { return new Date(dateStr) < new Date(new Date().toDateString()); } catch { return false; }
}

// 11.0 Client Actions — bespoke (not the generic TaggedTable). Oct 2026
// feedback Prompt 8: the client's own manual instructions/feedback lead,
// followed by a free-text communications note, then the auto-pulled project
// Actions sit in a collapsed-by-default "for reference" group (client/shared
// owned ones first within it). Links out to the source Action, description
// under the title, and the due date turns red once overdue.
export function ClientActionsTable({ rows, onChange, disabled, testId, narrative, onNarrativeChange }) {
    const [refOpen, setRefOpen] = useState(false);
    const list = rows || [];
    const update = (id, key, val) => onChange(list.map((r) => (r.id === id ? { ...r, [key]: val, edited: true } : r)));
    const remove = (id) => onChange(list.filter((r) => r.id !== id));
    const add = () => onChange([...list, { id: `manual-${Date.now()}`, source: "manual", responsible_party: "client" }]);
    const clientRows = list.filter((r) => r.source !== "auto");
    const otherRows = [...list.filter((r) => r.source === "auto")].sort(
        (a, b) => (["client", "shared"].includes(a.responsible_party) ? 0 : 1) - (["client", "shared"].includes(b.responsible_party) ? 0 : 1)
    );

    const Row = ({ row }) => {
        const overdue = _isOverdue(row.required_by);
        const isManual = !row.source_ref;
        return (
            <div className="p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-[220px]">
                        <span className="text-[11px] font-bold text-[#8B867E] shrink-0">{row.ref || "—"}</span>
                        {row.source === "auto" && <Badge tone="auto">Auto · from Actions</Badge>}
                        {isManual ? (
                            <TextInput value={row.action} disabled={disabled} placeholder="Action / information required"
                                       onChange={(v) => update(row.id, "action", v)} testId={testId && `${testId}-action-${row.id}`} />
                        ) : (
                            <a href={`/app/actions?open=${row.source_ref}`} target="_blank" rel="noreferrer"
                               data-testid={testId && `${testId}-link-${row.id}`}
                               className="text-[13px] font-bold text-[#1F1F1F] hover:text-[#C75A1B] hover:underline">
                                {row.action || "Untitled action"}
                            </a>
                        )}
                    </div>
                    {!disabled && (
                        <button data-testid={testId && `${testId}-remove-${row.id}`} onClick={() => remove(row.id)}
                                className="text-[#B33A2E] hover:text-[#8A2A21]"><Trash2 size={15} /></button>
                    )}
                </div>
                {isManual ? (
                    <TextInput value={row.description} disabled={disabled} textarea placeholder="Description"
                               onChange={(v) => update(row.id, "description", v)} testId={testId && `${testId}-desc-${row.id}`} />
                ) : row.description ? (
                    <div className="text-[12px] text-[#6F6A64]">{row.description}</div>
                ) : null}
                <div className="grid sm:grid-cols-2 gap-2 pt-1">
                    <Field label="Required by">
                        {isManual ? (
                            <TextInput value={row.required_by} disabled={disabled} type="date"
                                       onChange={(v) => update(row.id, "required_by", v)} testId={testId && `${testId}-due-${row.id}`} />
                        ) : (
                            <div className={`text-[13px] font-semibold px-2.5 py-1.5 ${overdue ? "text-[#B3261E]" : "text-[#1F1F1F]"}`}
                                 data-testid={testId && `${testId}-due-${row.id}`}>
                                {row.required_by || "—"}{overdue && " · Overdue"}
                            </div>
                        )}
                    </Field>
                    <Field label="Status / owner">
                        <TextInput value={row.status_owner} disabled={disabled || row.source === "auto"}
                                   onChange={(v) => update(row.id, "status_owner", v)} testId={testId && `${testId}-status-${row.id}`} />
                    </Field>
                </div>
            </div>
        );
    };

    const Group = ({ title, groupRows, groupTestId }) => (
        <div data-testid={groupTestId} className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
            <div className="px-3 py-2 bg-[#F5F3F0] text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">{title}</div>
            {groupRows.length === 0 ? (
                <div className="px-3 py-3 text-[13px] text-[#8B867E] italic">No entries.</div>
            ) : (
                <div className="divide-y divide-[#F0ECE5]">
                    {groupRows.map((row) => <Row key={row.id} row={row} />)}
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-3" data-testid={testId}>
            <Group title="Client instructions & feedback this week" groupRows={clientRows} groupTestId={testId && `${testId}-client-group`} />
            {!disabled && (
                <button data-testid={testId && `${testId}-add`} onClick={add}
                        className="flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold text-[#C75A1B] hover:bg-[#FBF3EC] w-full rounded-xl border border-dashed border-[#E5E0DA]">
                    <Plus size={14} /> Add client instruction
                </button>
            )}
            <CommentField value={narrative} onChange={onNarrativeChange} disabled={disabled}
                          label="Client communications this week (meetings, feedback, observations)"
                          addLabel="Add client communications note" testId={testId && `${testId}-narrative`} />
            <div className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
                <button type="button" data-testid={testId && `${testId}-reference-toggle`} onClick={() => setRefOpen((o) => !o)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-[#F5F3F0] text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">
                    <span>Open project actions (for reference) {otherRows.length > 0 && `· ${otherRows.length}`}</span>
                    <ChevronDown size={14} className={`transition-transform ${refOpen ? "rotate-180" : ""}`} />
                </button>
                {refOpen && (
                    otherRows.length === 0 ? (
                        <div data-testid={testId && `${testId}-reference-group`} className="px-3 py-3 text-[13px] text-[#8B867E] italic">No open project actions.</div>
                    ) : (
                        <div data-testid={testId && `${testId}-reference-group`} className="divide-y divide-[#F0ECE5]">
                            {otherRows.map((row) => <Row key={row.id} row={row} />)}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

// 10.0 Risks & Issues (Oct 2026 feedback Prompt 6) — bespoke card layout (not
// the generic TaggedTable) so each row has room for an age badge, a
// conditional closure note, and reviewer approve/reject actions. `canDecide`
// controls the approve/reject buttons and is independent of `disabled` —
// sign-off can happen on a report that is no longer a draft.
function RiskCard({ row, disabled, role, canDecide, onUpdate, onRemove, onDismiss, onDecide, testId }) {
    const [rejecting, setRejecting] = useState(false);
    const [comment, setComment] = useState("");
    const isIncident = (row.source_ref || "").startsWith("incident:");
    const isManual = row.source === "manual";
    const age = riskAgeDays(row.first_raised_at);
    const tone = riskAgeTone(age);
    const toneColors = { neutral: { bg: "#F0ECE5", fg: "#6F6A64" }, amber: { bg: "#FDF0DC", fg: "#B5720A" }, red: { bg: "#FBE3E1", fg: "#B3261E" } }[tone];
    const isSiteManager = role === "site_manager";
    const lockedForSiteManager = isSiteManager && isIncident;
    const statusOptions = isSiteManager ? ["Open", "Closed - awaiting sign-off"] : RISK_STATUS_OPTIONS;
    const awaitingSignoff = row.status === "Closed - awaiting sign-off";
    return (
        <div data-testid={testId && `${testId}-row-${row.id}`} className="rounded-xl border border-[#E5E0DA] bg-white p-3 space-y-2">
            <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold text-[#8B867E]">{row.ref || "—"}</span>
                    {row.source === "auto" && !row.edited && <Badge tone="auto">Suggested</Badge>}
                    {row.source === "carried" && <Badge tone="carried">{row.carried_from ? `From ${row.carried_from}` : "Carried"}</Badge>}
                    {age !== null && (row.status || "Open") !== "Closed" && (
                        <span data-testid={testId && `${testId}-age-${row.id}`}
                              className="px-1.5 py-0.5 rounded-full text-[10.5px] font-bold" style={{ background: toneColors.bg, color: toneColors.fg }}>
                            Open {age}d
                        </span>
                    )}
                    {awaitingSignoff && <Badge tone="suggested">Awaiting sign-off</Badge>}
                </div>
                <div className="flex items-center gap-2">
                    {onDismiss && (row.source === "auto" || row.source === "carried") && !disabled && (
                        <button onClick={() => onDismiss(row)} data-testid={testId && `${testId}-dismiss-${row.id}`}
                                className="text-[11px] font-semibold text-[#2A5DB0] hover:underline">Dismiss</button>
                    )}
                    {isManual && !disabled && (
                        <button onClick={() => onRemove(row.id)} data-testid={testId && `${testId}-remove-${row.id}`}
                                className="text-[#B33A2E] hover:text-[#8A2A21]"><Trash2 size={15} /></button>
                    )}
                </div>
            </div>
            <TextInput value={row.risk} disabled={disabled} textarea placeholder="Risk / issue"
                       onChange={(v) => onUpdate(row.id, "risk", v)} testId={testId && `${testId}-risk-${row.id}`} />
            <div className="grid sm:grid-cols-3 gap-2">
                <Field label="Impact">
                    <SelectInput value={row.impact} disabled={disabled} options={IMPACT_OPTIONS}
                                 onChange={(v) => onUpdate(row.id, "impact", v)} testId={testId && `${testId}-impact-${row.id}`} />
                </Field>
                <Field label="Status">
                    <SelectInput value={row.status || "Open"} disabled={disabled || lockedForSiteManager} options={statusOptions}
                                 onChange={(v) => onUpdate(row.id, "status", v)} testId={testId && `${testId}-status-${row.id}`} />
                    {lockedForSiteManager && <div className="text-[10.5px] text-[#8B867E] mt-1">Closure by reviewer only</div>}
                </Field>
                <Field label="Action / owner">
                    <TextInput value={row.action_owner} disabled={disabled}
                               onChange={(v) => onUpdate(row.id, "action_owner", v)} testId={testId && `${testId}-owner-${row.id}`} />
                </Field>
            </div>
            {awaitingSignoff && (
                <Field label="Closure note">
                    <TextInput value={row.closure_note} disabled={disabled} textarea placeholder="Why this risk is considered resolved"
                               onChange={(v) => onUpdate(row.id, "closure_note", v)} testId={testId && `${testId}-note-${row.id}`} />
                </Field>
            )}
            {row.reviewer_comment && (row.status || "Open") === "Open" && (
                <div data-testid={testId && `${testId}-reviewer-comment-${row.id}`} className="text-[12px] text-[#B3261E] bg-[#FBE3E1]/50 rounded-lg px-2.5 py-1.5">
                    Reviewer comment: {row.reviewer_comment}
                </div>
            )}
            {row.status === "Closed" && row.signed_off_by && (
                <div className="text-[11.5px] text-[#2E7D32]">Signed off by {row.signed_off_by}</div>
            )}
            {canDecide && awaitingSignoff && (
                <div className="flex items-center gap-3 pt-1.5 border-t border-[#F0ECE5]">
                    <button onClick={() => onDecide(row, "approve")} data-testid={testId && `${testId}-approve-${row.id}`}
                            className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#2E7D32] hover:underline">
                        <Check size={13} /> Approve closure
                    </button>
                    {!rejecting ? (
                        <button onClick={() => setRejecting(true)} data-testid={testId && `${testId}-reject-${row.id}`}
                                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#B3261E] hover:underline">
                            <Undo2 size={13} /> Send back to Open
                        </button>
                    ) : (
                        <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
                            <TextInput value={comment} onChange={setComment} placeholder="Comment for the Site Manager"
                                       testId={testId && `${testId}-reject-comment-${row.id}`} />
                            <button onClick={() => onDecide(row, "reject", comment)} data-testid={testId && `${testId}-reject-confirm-${row.id}`}
                                    className="text-[11.5px] font-semibold text-[#B3261E] hover:underline shrink-0">Confirm</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function RisksTable({ rows, onChange, disabled, role, canDecide, onDecide, onDismiss, testId }) {
    const list = rows || [];
    const update = (id, key, val) => onChange(list.map((r) => (r.id === id ? { ...r, [key]: val, edited: true } : r)));
    const remove = (id) => onChange(list.filter((r) => r.id !== id));
    const add = () => onChange([...list, { id: `manual-${Date.now()}`, source: "manual", status: "Open" }]);
    return (
        <div className="space-y-2" data-testid={testId}>
            {list.length === 0 && <div className="text-[13px] text-[#8B867E] italic px-1">No risks or issues recorded.</div>}
            {list.map((row) => (
                <RiskCard key={row.id} row={row} disabled={disabled} role={role} canDecide={canDecide}
                          onUpdate={update} onRemove={remove} onDismiss={onDismiss} onDecide={onDecide} testId={testId} />
            ))}
            {!disabled && (
                <button data-testid={testId && `${testId}-add`} onClick={add}
                        className="flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold text-[#C75A1B] hover:bg-[#FBF3EC] w-full rounded-xl border border-dashed border-[#E5E0DA]">
                    <Plus size={14} /> Add risk / issue
                </button>
            )}
        </div>
    );
}


export function CommentField({ value, onChange, disabled, testId, label = "Comment", addLabel = "Add comment" }) {
    const [open, setOpen] = useState(!!value);
    if (!open && !value) {
        if (disabled) return null;
        return (
            <button type="button" data-testid={testId && `${testId}-toggle`} onClick={() => setOpen(true)}
                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#C75A1B] hover:underline">
                <Plus size={12} /> {addLabel}
            </button>
        );
    }
    return (
        <Field label={label}>
            <TextInput value={value} disabled={disabled} onChange={onChange} testId={testId} />
        </Field>
    );
}

export function SectionCard({ n, title, action, children, testId, reviewNote }) {
    return (
        <section id={n ? `wsr-section-${n}` : undefined} data-testid={testId} className="bg-white rounded-2xl border border-[#E5E0DA] p-5 sm:p-6 space-y-4 shadow-brand-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-[15px] sm:text-base font-bold text-[#1F1F1F]"><span className="text-[#C75A1B]">{n}</span> {title}</h3>
                {action}
            </div>
            {reviewNote && ((reviewNote.comment || "").trim() || (reviewNote.actions || []).length > 0) && (
                <div data-testid={`wsr-section-reviewnote-${n}`} className="rounded-xl border p-3 space-y-1.5" style={{ background: "#FBE3E1", borderColor: "#F3C6C2" }}>
                    <div className="text-[10.5px] font-bold uppercase tracking-[.05em] text-[#B3261E]">Reviewer comment</div>
                    {reviewNote.comment && <p className="text-[12.5px] text-[#8A2A21]">{reviewNote.comment}</p>}
                    {(reviewNote.actions || []).map((a) => (
                        <div key={a.id} className="text-[11.5px] text-[#8A2A21] flex items-center gap-2 flex-wrap">
                            <span className="font-mono">{a.ref}</span><span className="font-semibold">{a.title}</span>
                            <span>· {a.owner || "Unassigned"}</span><span>· due {a.due_date || "—"}</span>
                        </div>
                    ))}
                </div>
            )}
            {children}
        </section>
    );
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const touchDist = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

// Full-screen click-to-expand preview for evidence photos. Shared by the
// editor's PhotoGrid and the reviewer's read-only PhotoGrid usage.
// Supports scroll-wheel zoom, pinch-to-zoom (touch), drag-to-pan once
// zoomed, and double-click/tap to toggle zoom.
function PhotoLightbox({ photos, index, onClose, onNavigate }) {
    const [scale, setScale] = useState(1);
    const [panPos, setPanPos] = useState({ x: 0, y: 0 });
    const panRef = useRef({ dragging: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0, moved: false });
    const pinchRef = useRef({ active: false, startDist: 0, startScale: 1 });
    const closeGuardRef = useRef(false);
    const scaleRef = useRef(1);
    const panPosRef = useRef({ x: 0, y: 0 });
    const bodyRef = useRef(null);
    scaleRef.current = scale;
    panPosRef.current = panPos;

    const resetZoom = () => { setScale(1); setPanPos({ x: 0, y: 0 }); };
    useEffect(() => { resetZoom(); }, [index]);

    const clampPan = (x, y, s) => {
        const maxX = (window.innerWidth * (s - 1)) / 2;
        const maxY = (window.innerHeight * (s - 1)) / 2;
        return { x: Math.min(maxX, Math.max(-maxX, x)), y: Math.min(maxY, Math.max(-maxY, y)) };
    };

    const setClampedScale = (next) => {
        const s = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
        setScale(s);
        if (s === 1) setPanPos({ x: 0, y: 0 });
        else setPanPos((p) => clampPan(p.x, p.y, s));
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft" && scaleRef.current === 1) onNavigate(-1);
            if (e.key === "ArrowRight" && scaleRef.current === 1) onNavigate(1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, onNavigate]);

    // Native (non-passive) wheel + touchmove listeners — React attaches these
    // passively by default, which silently blocks preventDefault() and lets
    // the page scroll behind the modal while the user is trying to zoom.
    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        const handleWheel = (e) => {
            e.preventDefault();
            setClampedScale(+(scaleRef.current + (e.deltaY > 0 ? -0.25 : 0.25)).toFixed(2));
        };
        const handleTouchMove = (e) => {
            if (e.touches.length === 2 && pinchRef.current.active) {
                e.preventDefault();
                const factor = touchDist(e.touches) / pinchRef.current.startDist;
                setClampedScale(+(pinchRef.current.startScale * factor).toFixed(2));
            } else if (e.touches.length === 1 && panRef.current.dragging) {
                e.preventDefault();
                const dx = e.touches[0].clientX - panRef.current.startX;
                const dy = e.touches[0].clientY - panRef.current.startY;
                if (Math.abs(dx) > 3 || Math.abs(dy) > 3) panRef.current.moved = true;
                setPanPos(clampPan(panRef.current.startPanX + dx, panRef.current.startPanY + dy, scaleRef.current));
            }
        };
        el.addEventListener("wheel", handleWheel, { passive: false });
        el.addEventListener("touchmove", handleTouchMove, { passive: false });
        return () => {
            el.removeEventListener("wheel", handleWheel);
            el.removeEventListener("touchmove", handleTouchMove);
        };
    }, [index]);

    if (index === null || index === undefined) return null;
    const photo = photos[index];
    if (!photo) return null;

    const onDoubleClick = (e) => {
        e.stopPropagation();
        setClampedScale(scale > 1 ? 1 : 2.5);
    };

    const stopIfMoved = () => {
        if (panRef.current.moved) { closeGuardRef.current = true; panRef.current.moved = false; }
    };

    const onMouseDown = (e) => {
        if (scale <= 1) return;
        panRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startPanX: panPos.x, startPanY: panPos.y, moved: false };
    };
    const onMouseMove = (e) => {
        if (!panRef.current.dragging) return;
        const dx = e.clientX - panRef.current.startX;
        const dy = e.clientY - panRef.current.startY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) panRef.current.moved = true;
        setPanPos(clampPan(panRef.current.startPanX + dx, panRef.current.startPanY + dy, scale));
    };
    const onMouseUp = () => { panRef.current.dragging = false; stopIfMoved(); };

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            pinchRef.current = { active: true, startDist: touchDist(e.touches), startScale: scale };
        } else if (e.touches.length === 1 && scale > 1) {
            panRef.current = { dragging: true, startX: e.touches[0].clientX, startY: e.touches[0].clientY, startPanX: panPos.x, startPanY: panPos.y, moved: false };
        }
    };
    const onTouchEnd = (e) => {
        if (e.touches.length < 2) pinchRef.current.active = false;
        if (e.touches.length < 1) { panRef.current.dragging = false; stopIfMoved(); }
    };

    const handleClose = () => {
        if (closeGuardRef.current) { closeGuardRef.current = false; return; }
        onClose();
    };

    const zoomPct = Math.round(scale * 100);

    return (
        <div data-testid="wsr-photo-lightbox" className="fixed inset-0 z-[100] bg-black/85 flex flex-col"
             onClick={handleClose}>
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 text-white shrink-0" onClick={(e) => e.stopPropagation()}>
                <span className="text-[12.5px] font-medium text-white/80">{index + 1} / {photos.length}</span>
                <div className="flex items-center gap-1.5">
                    <button type="button" data-testid="wsr-photo-lightbox-zoom-out" onClick={() => setClampedScale(scale - 0.5)}
                            disabled={scale <= MIN_ZOOM}
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center disabled:opacity-30">
                        <ZoomOut size={15} />
                    </button>
                    <span data-testid="wsr-photo-lightbox-zoom-level" className="text-[11.5px] font-semibold text-white/80 w-11 text-center tabular-nums">{zoomPct}%</span>
                    <button type="button" data-testid="wsr-photo-lightbox-zoom-in" onClick={() => setClampedScale(scale + 0.5)}
                            disabled={scale >= MAX_ZOOM}
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center disabled:opacity-30">
                        <ZoomIn size={15} />
                    </button>
                    {scale > 1 && (
                        <button type="button" data-testid="wsr-photo-lightbox-zoom-reset" onClick={resetZoom}
                                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center">
                            <RotateCcw size={14} />
                        </button>
                    )}
                    <button type="button" data-testid="wsr-photo-lightbox-close" onClick={onClose}
                            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center ml-1.5">
                        <X size={18} />
                    </button>
                </div>
            </div>
            <div ref={bodyRef} className="flex-1 flex items-center justify-center px-2 sm:px-10 min-h-0 overflow-hidden"
                 onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
                 onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                {photos.length > 1 && scale === 1 && (
                    <button type="button" data-testid="wsr-photo-lightbox-prev" onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
                            className="hidden sm:grid w-10 h-10 shrink-0 rounded-full bg-white/10 hover:bg-white/20 place-items-center text-white mr-2">
                        <ChevronLeft size={20} />
                    </button>
                )}
                <img src={photo.url} alt={photo.caption || "Evidence"} onClick={(e) => e.stopPropagation()} onDoubleClick={onDoubleClick}
                     draggable={false}
                     style={{ transform: `translate(${panPos.x}px, ${panPos.y}px) scale(${scale})`, cursor: scale > 1 ? "grab" : "zoom-in" }}
                     className="max-w-full max-h-full object-contain rounded-lg select-none touch-none" />
                {photos.length > 1 && scale === 1 && (
                    <button type="button" data-testid="wsr-photo-lightbox-next" onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
                            className="hidden sm:grid w-10 h-10 shrink-0 rounded-full bg-white/10 hover:bg-white/20 place-items-center text-white ml-2">
                        <ChevronRight size={20} />
                    </button>
                )}
            </div>
            <div className="px-4 sm:px-6 py-3 text-center shrink-0 space-y-0.5">
                {photo.caption && <div className="text-[13px] text-white/85">{photo.caption}</div>}
                <div className="text-[10.5px] text-white/40">Scroll or pinch to zoom · double-click to reset</div>
            </div>
        </div>
    );
}

export function PhotoGrid({ photos, apiBase, reportId, onCaption, onDelete, onUpload, disabled, pendingPhotos, onRetryPending, onDiscardPending, onDiscardAllPending }) {
    const list = photos || [];
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const lightboxPhotos = list.map((p) => ({ url: `${apiBase}/weekly-reports/${reportId}/photos/${p.id}/file`, caption: p.caption }));
    const navigate = (delta) => setLightboxIndex((i) => (i === null ? i : (i + delta + lightboxPhotos.length) % lightboxPhotos.length));
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                {list.map((p, idx) => (
                    <div key={p.id} className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
                        <button type="button" data-testid={`wsr-photo-image-${p.id}`} onClick={() => setLightboxIndex(idx)}
                                className="group relative w-full h-40 block cursor-zoom-in">
                            <img src={`${apiBase}/weekly-reports/${reportId}/photos/${p.id}/file`} alt={p.caption || "Evidence"}
                                 className="w-full h-40 object-cover" />
                            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </span>
                        </button>
                        <div className="p-2 space-y-1">
                            <TextInput value={p.caption} disabled={disabled} placeholder="Caption"
                                       onChange={(v) => onCaption(p.id, v, false)} onBlur={(e) => onCaption(p.id, e.target.value, true)}
                                       testId={`wsr-photo-caption-${p.id}`} />
                            {!disabled && (
                                <button data-testid={`wsr-photo-delete-${p.id}`} onClick={() => onDelete(p.id)}
                                        className="text-[11px] text-[#B33A2E] flex items-center gap-1"><X size={12} /> Remove</button>
                            )}
                        </div>
                    </div>
                ))}
                {!disabled && (
                    <label className="rounded-xl border border-dashed border-[#D8D3CB] flex flex-col items-center justify-center gap-1.5 h-full min-h-[160px] cursor-pointer text-[#8B867E] hover:border-[#C75A1B] hover:text-[#C75A1B]">
                        <Camera size={20} />
                        <span className="text-[11.5px] font-semibold">Add photos</span>
                        <input data-testid="wsr-photo-input" type="file" accept="image/*" multiple className="hidden"
                               onChange={(e) => { if (e.target.files?.length) onUpload(e.target.files); e.target.value = ""; }} />
                    </label>
                )}
            </div>
            {pendingPhotos?.length > 0 && (
                <div data-testid="wsr-photo-sync-status" className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 border" style={{ background: "#FBEFE3", borderColor: "#F0DCC2" }}>
                    <CloudOff size={15} className="text-[#9F4212] shrink-0" />
                    <p className="text-[12px] text-[#9F4212] font-semibold flex-1">
                        {pendingPhotos.length} photo{pendingPhotos.length !== 1 ? "s" : ""} {pendingPhotos.every((p) => p.failed) ? "failed to upload — tap below to retry or discard." : "saved on this device — uploading…"}
                    </p>
                    {!disabled && onDiscardAllPending && (
                        <button type="button" data-testid="wsr-discard-all-pending-photos"
                                onClick={() => { if (window.confirm(`Discard all ${pendingPhotos.length} pending photo(s)? They will not be uploaded.`)) onDiscardAllPending(); }}
                                className="text-[11px] font-semibold text-[#9F4212] hover:underline shrink-0">
                            Discard all
                        </button>
                    )}
                </div>
            )}
            <PendingPhotoThumbs pending={pendingPhotos} testId="wsr-pending-photos" onRetry={onRetryPending} onDiscard={disabled ? null : onDiscardPending} />
            <PhotoLightbox photos={lightboxPhotos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={navigate} />
        </div>
    );
}

const SOURCE_ICON = { form_instances: FileText, incidents: FileText, actions: FileText, documents: ImageIcon };

// Prompt 12, item A — "Add from this week's photos": pick a photo already on
// the system (inspection, incident, action evidence or a photo document) and
// add it to the report BY REFERENCE (no re-upload).
export function PhotoSourcePickerDialog({ open, onClose, reportId, onAdd }) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [justAdded, setJustAdded] = useState(new Set());

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setJustAdded(new Set());
        api.get(`/weekly-reports/${reportId}/photo-sources`)
            .then((r) => setGroups(r.data.groups || []))
            .catch(() => toast.error("Could not load candidate photos"))
            .finally(() => setLoading(false));
    }, [open, reportId]);

    if (!open) return null;

    const add = (item) => {
        onAdd({
            id: `linked-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            storage_path: item.storage_path, filename: item.filename, mime_type: item.mime_type,
            caption: item.caption || "", uploaded_at: new Date().toISOString(),
            source: "linked", source_ref: item.ref, origin_label: item.label,
        });
        setJustAdded((s) => new Set(s).add(item.ref));
        toast.success("Photo added — edit its caption below");
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div data-testid="wsr-photo-picker-dialog" className="bg-white rounded-[16px] shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-base font-bold text-[#1F1F1F]">Add from this week's photos</h3>
                    <button data-testid="wsr-photo-picker-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-5">
                    {loading && (
                        <div className="flex items-center gap-2 text-[13px] text-[#8B867E] py-6 justify-center"><Loader2 size={16} className="animate-spin" /> Loading…</div>
                    )}
                    {!loading && groups.length === 0 && (
                        <p className="text-[13px] text-[#8B867E] italic py-6 text-center">No photos found on this project for this reporting week yet.</p>
                    )}
                    {groups.map((g) => {
                        const Icon = SOURCE_ICON[g.source] || ImageIcon;
                        return (
                            <div key={g.source} data-testid={`wsr-photo-picker-group-${g.source}`}>
                                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] mb-2 flex items-center gap-1.5"><Icon size={12} /> {g.label}</div>
                                <div className="grid grid-cols-3 gap-2.5">
                                    {g.items.map((item) => {
                                        const added = item.already_added || justAdded.has(item.ref);
                                        return (
                                            <div key={item.ref} className="rounded-lg border border-[#E5E0DA] overflow-hidden bg-white">
                                                <img src={`${API}${item.url}`} alt={item.caption || item.filename || "Photo"} className="w-full h-24 object-cover" />
                                                <div className="p-1.5 space-y-1">
                                                    <div className="text-[10.5px] font-semibold text-[#1F1F1F] truncate" title={item.label}>{item.label}</div>
                                                    <div className="text-[10px] text-[#8B867E]">{item.date}</div>
                                                    <button data-testid={`wsr-photo-picker-add-${item.ref}`} disabled={added} onClick={() => add(item)}
                                                            className="w-full text-[11px] font-semibold rounded-md py-1 disabled:opacity-50"
                                                            style={added ? { background: "#F0ECE5", color: "#8B867E" } : { background: "#FBF3EC", color: "#C75A1B" }}>
                                                        {added ? "Added" : "+ Add"}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// Prompt 12, item B — "Link a record": pick a project Document or one of this
// week's form instances (daily checklist, RAMS briefing, etc.) to reference
// in the 12.0 Attachments register.
export function LinkRecordDialog({ open, onClose, projectId, weekStart, weekEnd, onAdd }) {
    const [tab, setTab] = useState("documents");
    const [documents, setDocuments] = useState([]);
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        Promise.all([
            api.get("/documents", { params: { project_id: projectId } }),
            api.get("/forms/instances", { params: { project_id: projectId } }),
        ]).then(([docsRes, instRes]) => {
            setDocuments(docsRes.data || []);
            const weekInsts = (instRes.data || []).filter((i) => {
                const d = (i.submitted_at || i.created_at || "").slice(0, 10);
                return d >= weekStart && d <= weekEnd;
            });
            setInstances(weekInsts);
        }).catch(() => toast.error("Could not load records")).finally(() => setLoading(false));
    }, [open, projectId, weekStart, weekEnd]);

    if (!open) return null;

    const pick = (record, kind) => {
        onAdd({
            id: `manual-${Date.now()}`, source: "linked",
            title: record.title || record.template_name || "Untitled record", description: "",
            reference: kind === "document" ? `/app/documents?open=${record.id}` : `/app/inspections/${record.id}`,
        });
        onClose();
        toast.success("Record linked");
    };

    const list = tab === "documents" ? documents : instances;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div data-testid="wsr-link-record-dialog" className="bg-white rounded-[16px] shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-base font-bold text-[#1F1F1F] flex items-center gap-1.5"><Link2 size={15} /> Link a record</h3>
                    <button data-testid="wsr-link-record-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>
                <div className="px-5 pt-3 flex gap-1.5">
                    {[["documents", "Documents"], ["instances", "This week's form instances"]].map(([k, lbl]) => (
                        <button key={k} data-testid={`wsr-link-record-tab-${k}`} onClick={() => setTab(k)}
                                className="px-3 py-1.5 rounded-full text-[12px] font-semibold border"
                                style={tab === k ? { background: "#FBF3EC", color: "#C75A1B", borderColor: "#C75A1B" } : { background: "#fff", color: "#8B867E", borderColor: "#E5E0DA" }}>
                            {lbl}
                        </button>
                    ))}
                </div>
                <div className="p-5 space-y-1.5">
                    {loading && (
                        <div className="flex items-center gap-2 text-[13px] text-[#8B867E] py-6 justify-center"><Loader2 size={16} className="animate-spin" /> Loading…</div>
                    )}
                    {!loading && list.length === 0 && (
                        <p className="text-[13px] text-[#8B867E] italic py-6 text-center">
                            {tab === "documents" ? "No documents on this project yet." : "No form instances submitted this reporting week yet."}
                        </p>
                    )}
                    {!loading && list.map((r) => (
                        <button key={r.id} data-testid={`wsr-link-record-pick-${r.id}`}
                                onClick={() => pick(r, tab === "documents" ? "document" : "instance")}
                                className="w-full text-left px-3 py-2 rounded-lg border border-[#E5E0DA] hover:border-[#C75A1B] hover:bg-[#FBF3EC] flex items-center justify-between gap-2">
                            <span className="text-[13px] font-semibold text-[#1F1F1F] truncate">{r.title || r.template_name || "Untitled"}</span>
                            <span className="text-[11px] text-[#8B867E] shrink-0">{(r.submitted_at || r.created_at || "").slice(0, 10)}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// 3.0 Works Progress — bespoke (Oct 2026 Section 3.0 rework). Fixed "Planned
// vs Actual" row leads, undeletable, overall project position: auto-filled
// read-only from the works tracker when available, else editable 0-100
// inputs. Variance (Actual - Planned) is always computed live client-side,
// for both the fixed row and any manually added work-package rows.
export function ProgrammeWorksProgressTable({ pva, onPvaChange, rows, onRowsChange, disabled, testId }) {
    const list = rows || [];
    const update = (idx, key, val) => onRowsChange(list.map((r, i) => (i === idx ? { ...r, [key]: val, edited: true } : r)));
    const remove = (idx) => onRowsChange(list.filter((_, i) => i !== idx));
    const add = () => onRowsChange([...list, { id: `manual-${Date.now()}`, source: "manual", work_package: "", planned_pct: "", actual_pct: "", status: "" }]);
    const isAuto = pva?.mode === "auto";
    const pvaVariance = programmeVariance(pva);
    const pvaTone = varianceTone(pvaVariance);
    const toneClass = { green: "text-[#1E7A34]", amber: "text-[#B8860B]", red: "text-[#B3261E]", neutral: "text-[#9A968F]" };
    const fmtVariance = (v) => (v === null ? "—" : `${v > 0 ? "+" : ""}${v} pts`);
    const rowVariance = (r) => fmtVariance(programmeVariance({ planned_pct: r.planned_pct, actual_pct: r.actual_pct }));
    return (
        <div data-testid={testId} className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="bg-[#F5F3F0] text-left">
                            <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Work package</th>
                            <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] w-28">Planned %</th>
                            <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] w-28">Actual %</th>
                            <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] w-28">Variance</th>
                            <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E]">Status</th>
                            {!disabled && <th className="w-8" />}
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-t border-[#F0ECE5] align-top bg-[#FBF9F6]">
                            <td className="px-3 py-2.5 font-semibold text-[#1F1F1F]">
                                <div className="flex items-center gap-2 flex-wrap">
                                    Planned vs Actual
                                    {isAuto ? <Badge tone="auto">Auto · from project works tracker</Badge> : <Badge tone="fixed">Fixed</Badge>}
                                </div>
                            </td>
                            <td className="px-2 py-1.5">
                                {isAuto ? <div className="px-2 py-1.5 text-[#1F1F1F]">{pva.planned_pct}%</div> :
                                    <input type="number" min="0" max="100" disabled={disabled} data-testid={testId && `${testId}-planned`}
                                           value={pva?.planned_pct ?? ""} onChange={(e) => onPvaChange({ ...pva, planned_pct: e.target.value })}
                                           className={INPUT} />}
                            </td>
                            <td className="px-2 py-1.5">
                                {isAuto ? <div className="px-2 py-1.5 text-[#1F1F1F]">{pva.actual_pct}%</div> :
                                    <input type="number" min="0" max="100" disabled={disabled} data-testid={testId && `${testId}-actual`}
                                           value={pva?.actual_pct ?? ""} onChange={(e) => onPvaChange({ ...pva, actual_pct: e.target.value })}
                                           className={INPUT} />}
                            </td>
                            <td className={`px-3 py-2.5 font-bold ${toneClass[pvaTone]}`} data-testid={testId && `${testId}-variance`}>
                                {fmtVariance(pvaVariance)}
                            </td>
                            <td className="px-3 py-2.5 text-[#6F6A64]">Overall</td>
                            {!disabled && <td />}
                        </tr>
                        {list.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-3 text-[#8B867E] italic">No work packages yet.</td></tr>
                        )}
                        {list.map((row, idx) => {
                            const isReadOnly = disabled || row.source === "auto";
                            return (
                                <tr key={row.id || idx} className="border-t border-[#F0ECE5] align-top">
                                    <td className="px-2 py-1.5">
                                        {row.source === "auto" && <Badge tone="auto">Auto · from Works tracker</Badge>}
                                        <TextInput value={row.work_package} disabled={isReadOnly} onChange={(v) => update(idx, "work_package", v)} testId={testId && `${testId}-${idx}-work_package`} />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <TextInput value={row.planned_pct} disabled={isReadOnly} onChange={(v) => update(idx, "planned_pct", v)} testId={testId && `${testId}-${idx}-planned_pct`} />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <TextInput value={row.actual_pct} disabled={isReadOnly} onChange={(v) => update(idx, "actual_pct", v)} testId={testId && `${testId}-${idx}-actual_pct`} />
                                    </td>
                                    <td className="px-3 py-2.5 text-[#6F6A64]" data-testid={testId && `${testId}-${idx}-variance`}>
                                        {row.source === "auto" ? row.variance : rowVariance(row)}
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <TextInput value={row.status} disabled={isReadOnly} onChange={(v) => update(idx, "status", v)} testId={testId && `${testId}-${idx}-status`} />
                                    </td>
                                    {!disabled && (
                                        <td className="px-2 py-1.5 text-center">
                                            <button data-testid={testId && `${testId}-remove-${idx}`} onClick={() => remove(idx)} className="text-[#B33A2E] hover:text-[#8A2A21]"><Trash2 size={15} /></button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {!disabled && (
                <button data-testid={testId && `${testId}-add`} onClick={add}
                        className="flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold text-[#C75A1B] hover:bg-[#FBF3EC] w-full">
                    <Plus size={14} /> Add work package
                </button>
            )}
        </div>
    );
}

function _actionStatusTone(status) {
    if (status === "Complete") return "green";
    if (status === "Blocked") return "red";
    if (status === "In progress") return "blue";
    return "neutral";
}

// 3.0 Delay, Impact & Recovery — bespoke (Oct 2026 rework). Required only
// when behind schedule (greyed + "Not required" hint otherwise). Recovery
// Actions is a live-linked list backed by the Actions module — link an
// existing open action or create a new one inline.
export function DelayImpactRecoverySection({
    behind, delay, onDelayChange, impact, onImpactChange,
    recoveryActions, onLinkAction, onUnlinkAction, onCreateAction, candidates, onSearchCandidates,
    disabled, testId,
}) {
    const [showPicker, setShowPicker] = useState(false);
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", owner: "", due_date: "", priority: "Medium" });
    const daysLost = parseFloat(impact?.days_lost || "0") || 0;

    return (
        <div data-testid={testId} className={`space-y-3 rounded-xl border p-3 ${behind ? "border-[#E5E0DA] bg-white" : "border-[#EDEAE4] bg-[#FAF9F6]"}`}>
            {!behind && (
                <div data-testid={testId && `${testId}-not-required-hint`} className="text-[11.5px] font-semibold text-[#9A968F] uppercase tracking-wide">
                    Not required – on or ahead of plan
                </div>
            )}
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${!behind ? "opacity-60" : ""}`}>
                <Field label={`Delay reason${behind ? " *" : ""}`}>
                    <SelectInput value={delay?.reason} disabled={disabled} options={DELAY_REASON_OPTIONS}
                                 onChange={(v) => onDelayChange({ ...delay, reason: v })} testId={testId && `${testId}-delay-reason`} />
                </Field>
                <Field label="Notes / required action">
                    <TextInput value={delay?.note} disabled={disabled} textarea placeholder="[Explain root cause]"
                               onChange={(v) => onDelayChange({ ...delay, note: v })} testId={testId && `${testId}-delay-note`} />
                </Field>
                <Field label={`Programme impact${behind ? " *" : ""}`}>
                    <TextInput value={impact?.days_lost} disabled={disabled} placeholder="[Days lost / none]"
                               onChange={(v) => onImpactChange({ ...impact, days_lost: v })} testId={testId && `${testId}-impact-days`} />
                </Field>
                <Field label={`Notes / required action${daysLost > 0 ? " *" : ""}`}>
                    <TextInput value={impact?.notes} disabled={disabled} textarea placeholder="[Affected areas / knock-on effects]"
                               onChange={(v) => onImpactChange({ ...impact, notes: v })} testId={testId && `${testId}-impact-notes`} />
                </Field>
            </div>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[.04em] text-[#8B867E] mb-1.5">Recovery actions{behind ? " *" : ""}</div>
                <div className="rounded-lg border border-[#E5E0DA] overflow-hidden bg-white">
                    {recoveryActions.length === 0 ? (
                        <div data-testid={testId && `${testId}-recovery-empty`} className="px-3 py-3 text-[13px] text-[#8B867E] italic">No recovery actions linked yet.</div>
                    ) : (
                        <table className="w-full text-[12.5px]">
                            <thead><tr className="bg-[#F5F3F0] text-left">
                                <th className="px-2 py-1.5 text-[10.5px] font-semibold uppercase text-[#8B867E]">Ref</th>
                                <th className="px-2 py-1.5 text-[10.5px] font-semibold uppercase text-[#8B867E]">Title</th>
                                <th className="px-2 py-1.5 text-[10.5px] font-semibold uppercase text-[#8B867E]">Owner</th>
                                <th className="px-2 py-1.5 text-[10.5px] font-semibold uppercase text-[#8B867E]">Due</th>
                                <th className="px-2 py-1.5 text-[10.5px] font-semibold uppercase text-[#8B867E]">Status</th>
                                {!disabled && <th className="w-8" />}
                            </tr></thead>
                            <tbody>
                                {recoveryActions.map((a) => (
                                    <tr key={a.id} className="border-t border-[#F0ECE5]" data-testid={testId && `${testId}-row-${a.id}`}>
                                        <td className="px-2 py-1.5 font-mono text-[#6F6A64]">{a.ref}</td>
                                        <td className="px-2 py-1.5">{a.title}</td>
                                        <td className="px-2 py-1.5 text-[#6F6A64]">{a.owner || "—"}</td>
                                        <td className="px-2 py-1.5 text-[#6F6A64]">{a.due_date || "—"}</td>
                                        <td className="px-2 py-1.5"><StatusPill tone={_actionStatusTone(a.status)}>{a.status}</StatusPill></td>
                                        {!disabled && (
                                            <td className="px-2 py-1.5 text-center">
                                                <button data-testid={testId && `${testId}-unlink-${a.id}`} onClick={() => onUnlinkAction(a.id)} className="text-[#B33A2E] hover:text-[#8A2A21]"><Trash2 size={14} /></button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {!disabled && (
                    <div className="flex items-center gap-3 mt-2">
                        <button type="button" data-testid={testId && `${testId}-link-toggle`} onClick={() => { setShowPicker((v) => !v); setShowCreate(false); }}
                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#2A5DB0] hover:underline"><Link2 size={13} /> Link existing action</button>
                        <button type="button" data-testid={testId && `${testId}-create-toggle`} onClick={() => { setShowCreate((v) => !v); setShowPicker(false); }}
                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#C75A1B] hover:underline"><Plus size={13} /> Create new action</button>
                    </div>
                )}
                {showPicker && (
                    <div className="mt-2 rounded-lg border border-[#E5E0DA] bg-white p-2.5 space-y-2" data-testid={testId && `${testId}-picker`}>
                        <input data-testid={testId && `${testId}-picker-search`} value={search} placeholder="Search open actions on this project…"
                               onChange={(e) => { setSearch(e.target.value); onSearchCandidates(e.target.value); }}
                               className={INPUT} />
                        {(candidates || []).length === 0 ? (
                            <div className="text-[12px] text-[#9A968F] italic px-1">No matching open actions.</div>
                        ) : candidates.map((c) => (
                            <button type="button" key={c.id} data-testid={testId && `${testId}-candidate-${c.id}`}
                                    onClick={() => { onLinkAction(c.id); setShowPicker(false); setSearch(""); }}
                                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[#F5F3F0] text-[12.5px] flex items-center justify-between gap-2">
                                <span className="truncate"><span className="font-mono text-[#8B867E] mr-1.5">{c.ref}</span>{c.title}</span>
                                <span className="text-[11px] text-[#9A968F] shrink-0">{c.owner || "Unassigned"}</span>
                            </button>
                        ))}
                    </div>
                )}
                {showCreate && (
                    <div className="mt-2 rounded-lg border border-[#E5E0DA] bg-white p-2.5 space-y-2" data-testid={testId && `${testId}-create-form`}>
                        <input data-testid={testId && `${testId}-create-title`} placeholder="Title" value={form.title}
                               onChange={(e) => setForm({ ...form, title: e.target.value })} className={INPUT} />
                        <textarea data-testid={testId && `${testId}-create-description`} placeholder="Description" value={form.description}
                               onChange={(e) => setForm({ ...form, description: e.target.value })} className={INPUT} rows={2} />
                        <div className="grid grid-cols-3 gap-2">
                            <input data-testid={testId && `${testId}-create-owner`} placeholder="Owner" value={form.owner}
                                   onChange={(e) => setForm({ ...form, owner: e.target.value })} className={INPUT} />
                            <input type="date" data-testid={testId && `${testId}-create-due`} value={form.due_date}
                                   onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={INPUT} />
                            <select data-testid={testId && `${testId}-create-priority`} value={form.priority}
                                    onChange={(e) => setForm({ ...form, priority: e.target.value })} className={INPUT}>
                                {["Low", "Medium", "High"].map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <button type="button" data-testid={testId && `${testId}-create-submit`} disabled={!form.title.trim()}
                                onClick={() => { onCreateAction(form); setForm({ title: "", description: "", owner: "", due_date: "", priority: "Medium" }); setShowCreate(false); }}
                                className="px-3 py-1.5 rounded-lg text-white text-[12.5px] font-semibold disabled:opacity-50" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            Create action
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
